/**
 * Module 132 (WP-132.1) — Milestones, pure.
 *
 * Lifetime goals on progress the player makes anyway. Each milestone is one
 * counter and a ladder: Bronze → Silver → Gold → Platinum, then one ★ step for
 * every `every` more, forever (CLAUDE.md D2, D13). Every rung is a LEVEL here:
 * index 0–3 are the tiers, index 4 is ★1, 5 is ★2 and so on. Each level with
 * coins is claimed once, ever, with Claim (D4).
 *
 * Module 118's quests are the first rung of the matching milestone (D1). Their
 * state stays in the save and is read live as `legacy`: a claimed quest is a
 * claimed tier (paid under `blockjam_quest_<id>`, never again), and a complete
 * quest is a reached tier. So no quest pays twice, whichever UI claimed it.
 *
 * Counters move only on events the plugin already raises. Per-move events
 * (gates, blocks) change memory only; the move commit (Module 129) saves them.
 */
import { MILESTONES, MILESTONE_MAX_STEP, MILESTONE_STEP_COINS, MILESTONE_TIER_NAMES, QUEST_MOVES_SPARE, } from '../constants/economy';
export const EMPTY_MILESTONES = { counters: {}, claimed: [], run: 0, loginRun: 0 };
/** Which tier each Module 118 quest became (D1). Quest ids stay as server kinds. */
export const LEGACY_QUEST_TIER = {
    win_5: 'wins_bronze',
    win_50: 'wins_silver',
    reach_puzzle_20: 'reach_puzzle_bronze',
    win_streak_3: 'win_run_bronze',
    win_moves_spare: 'moves_spare_bronze',
    powered_gate_3: 'powered_gates_bronze',
    use_powerup: 'powerups_used_bronze',
    daily_finish: 'dailies_cleared_bronze',
    daily_streak_3: 'daily_streak_bronze',
    play_intermediate: 'medium_wins_bronze',
};
/** The targets Module 118 shipped, so a complete quest can be recognised without its registry. */
const LEGACY_QUEST_TARGET = {
    win_5: 5,
    win_50: 50,
    reach_puzzle_20: 20,
    win_streak_3: 3,
    win_moves_spare: 1,
    powered_gate_3: 3,
    use_powerup: 1,
    daily_finish: 1,
    daily_streak_3: 3,
    play_intermediate: 1,
};
/**
 * Module 131 (WP-131.10, D25/D28) — each track's Reach badge. Beginner keeps the
 * old any-track id, so its paid tiers, server rows and counter carry over.
 */
export const TRACK_REACH = {
    beginner: 'reach_puzzle',
    intermediate: 'medium_reach',
    expert: 'hard_reach',
};
/**
 * WP-131.10 — raise each track's Reach counter to the highest puzzle that track
 * proves (`puzzleByTrack`). Only ever raises, so nothing earned is taken back;
 * run on every load, for seeded and saved milestones alike. Unchanged ⇒ the same
 * object (no save is caused by it). Levels it reaches are earned without a date,
 * like any seeded level.
 */
export function withTrackReach(state, puzzleByTrack) {
    let counters = null;
    for (const [track, id] of Object.entries(TRACK_REACH)) {
        const n = Math.min(COUNTER_CAP, Math.floor(puzzleByTrack[track] ?? 0));
        if (n > counterOf(state, id))
            (counters ?? (counters = { ...state.counters }))[id] = n;
    }
    return counters ? { ...state, counters } : state;
}
export function trackLadder(state, track) {
    const def = milestoneDef(TRACK_REACH[track]);
    if (!def)
        return null;
    const next = nextTarget(state, def.id);
    if (!next)
        return null;
    return {
        prev: next.index > 0 ? levelTarget(def, next.index - 1) : 0,
        next: next.target,
        value: next.value,
        index: next.index,
    };
}
/** Counters never grow past this (a save can't be made to hold a silly number). */
const COUNTER_CAP = 1000000000;
export function milestoneDef(id) {
    return MILESTONES.find((m) => m.id === id);
}
export function counterOf(state, id) {
    return state.counters[id] ?? 0;
}
/** The level key for a rung: a tier name, then `p1`, `p2`… */
export function levelKey(def, index) {
    return index < def.tiers.length
        ? `${def.id}_${MILESTONE_TIER_NAMES[index]}`
        : `${def.id}_p${index - def.tiers.length + 1}`;
}
/** Coins for a rung. A badge-only milestone (no coins) pays nothing on any rung. */
export function levelCoins(def, index) {
    if (def.coins.length === 0)
        return 0;
    return index < def.tiers.length ? def.coins[index] : MILESTONE_STEP_COINS;
}
/** The counter value a rung needs. */
export function levelTarget(def, index) {
    if (index < def.tiers.length)
        return def.tiers[index];
    return def.tiers[def.tiers.length - 1] + (index - def.tiers.length + 1) * def.every;
}
/** How many rungs a counter value has reached (tiers, then ★ steps up to the server's cap). */
export function levelsForValue(def, value) {
    let tiers = 0;
    while (tiers < def.tiers.length && value >= def.tiers[tiers])
        tiers += 1;
    if (tiers < def.tiers.length || def.every <= 0)
        return tiers;
    const last = def.tiers[def.tiers.length - 1];
    return tiers + Math.min(MILESTONE_MAX_STEP, Math.floor((value - last) / def.every));
}
/** The highest rung index + 1 the legacy quests vouch for (complete or paid). */
function legacyLevels(def, legacy) {
    if (!legacy)
        return 0;
    let levels = 0;
    for (const [questId, key] of Object.entries(LEGACY_QUEST_TIER)) {
        const done = legacy.claimed.includes(questId) ||
            (legacy.progress[questId] ?? 0) >= LEGACY_QUEST_TARGET[questId];
        if (!done)
            continue;
        for (let i = 0; i < def.tiers.length; i += 1) {
            if (levelKey(def, i) === key)
                levels = Math.max(levels, i + 1);
        }
    }
    return levels;
}
/** Rungs reached: by the counter, or vouched for by a quest (a quest's tier is never taken back). */
export function levelsReached(state, def, legacy) {
    return Math.max(levelsForValue(def, counterOf(state, def.id)), legacyLevels(def, legacy));
}
/** D9 — the `badge_points` leaderboard's points per tier, Bronze first; each ★ step is 1. */
export const BADGE_TIER_POINTS = [1, 2, 3, 5];
export const BADGE_STEP_POINTS = 1;
/**
 * WP-132.8 — badge points: every tier reached on every badge (Bronze 1, Silver
 * 2, Gold 3, Platinum 5) plus 1 per ★ step. Reached as the popup counts it, so
 * a tier a Module 118 quest vouched for counts too.
 */
export function badgePoints(state, legacy) {
    let points = 0;
    for (const def of MILESTONES) {
        const reached = levelsReached(state, def, legacy);
        for (let i = 0; i < reached; i += 1) {
            points += i < BADGE_TIER_POINTS.length ? BADGE_TIER_POINTS[i] : BADGE_STEP_POINTS;
        }
    }
    return points;
}
/** Paid already: in this state, or as the quest it absorbed. */
export function isLevelClaimed(state, key, legacy) {
    if (state.claimed.includes(key))
        return true;
    if (!legacy)
        return false;
    return legacy.claimed.some((questId) => LEGACY_QUEST_TIER[questId] === key);
}
/** Reached, unpaid rungs with coins — what Claim buttons and the "!" count read. */
export function claimable(state, legacy) {
    const out = [];
    for (const def of MILESTONES) {
        const reached = levelsReached(state, def, legacy);
        for (let i = 0; i < reached; i += 1) {
            const coins = levelCoins(def, i);
            const key = levelKey(def, i);
            if (coins > 0 && !isLevelClaimed(state, key, legacy)) {
                out.push({ id: def.id, key, index: i, coins });
            }
        }
    }
    return out;
}
/** Pay one rung: the new state and the coins, or null when it isn't claimable. */
export function claimLevel(state, key, legacy) {
    const level = claimable(state, legacy).find((l) => l.key === key);
    if (!level)
        return null;
    return { state: { ...state, claimed: [...state.claimed, key] }, level };
}
/**
 * The next rung to reach, or null when there is none (a one-off that is done,
 * or ★999). One function for the popup, the win line and the home peek, so
 * they can never disagree.
 */
export function nextTarget(state, id, legacy) {
    const def = milestoneDef(id);
    if (!def)
        return null;
    const index = levelsReached(state, def, legacy);
    const maxLevels = def.every > 0 ? def.tiers.length + MILESTONE_MAX_STEP : def.tiers.length;
    if (index >= maxLevels)
        return null;
    return {
        id,
        key: levelKey(def, index),
        index,
        kind: index < def.tiers.length ? 'tier' : 'step',
        target: levelTarget(def, index),
        value: counterOf(state, id),
        coins: levelCoins(def, index),
    };
}
/** The counter changes one event makes, as `id → new value`. */
function counterChanges(state, evt, run, loginRun) {
    const c = (id) => counterOf(state, id);
    switch (evt.type) {
        case 'win': {
            const next = {
                wins: c('wins') + 1,
                win_run: Math.max(c('win_run'), run),
            };
            if (evt.movesLeft >= QUEST_MOVES_SPARE)
                next.moves_spare = c('moves_spare') + 1;
            if (evt.powerupFree)
                next.no_powerup_wins = c('no_powerup_wins') + 1;
            if (evt.track === 'intermediate')
                next.medium_wins = c('medium_wins') + 1;
            if (evt.track === 'expert')
                next.hard_wins = c('hard_wins') + 1;
            return next;
        }
        case 'gatesPowered':
            return evt.count > 0 ? { powered_gates: c('powered_gates') + evt.count } : {};
        case 'powerupUsed':
            return { powerups_used: c('powerups_used') + 1 };
        case 'dailyCleared':
            return {
                dailies_cleared: c('dailies_cleared') + 1,
                daily_streak: Math.max(c('daily_streak'), evt.streak),
            };
        case 'puzzleStarted': {
            // Module 131 (WP-131.10) — one Reach badge per track. `reach_puzzle` is the
            // Beginner badge now; it no longer moves on the other tracks.
            const id = TRACK_REACH[evt.track];
            return { [id]: Math.max(c(id), evt.puzzle) };
        }
        case 'blocksCleared':
            return evt.count > 0 ? { blocks_cleared: c('blocks_cleared') + evt.count } : {};
        case 'coinsSpent':
            return evt.amount > 0 ? { coins_spent: c('coins_spent') + Math.floor(evt.amount) } : {};
        case 'loginClaimed':
            return { login_streak: Math.max(c('login_streak'), loginRun) };
        case 'howToExplored':
            return { howto_explored: 1 };
        case 'avatarUnlocked':
            return { avatar_unlocked: c('avatar_unlocked') + 1 };
        case 'missionsCompleted':
            return evt.count > 0 ? { missions_completed: c('missions_completed') + evt.count } : {};
        case 'attemptBroken':
            return {};
    }
}
/**
 * `today` is the UTC date (`YYYY-MM-DD`) the caller read from its clock; every
 * level this event earns is stamped with it. Null = unknown, nothing stamped.
 */
export function applyEvent(state, evt, today = null) {
    var _a;
    let run = state.run;
    if (evt.type === 'win')
        run = Math.min(COUNTER_CAP, run + 1);
    if (evt.type === 'attemptBroken')
        run = 0;
    let loginRun = state.loginRun;
    if (evt.type === 'loginClaimed')
        loginRun = evt.continued ? Math.min(COUNTER_CAP, loginRun + 1) : 1;
    const counters = { ...state.counters };
    const earned = [];
    let changed = run !== state.run || loginRun !== state.loginRun;
    const next = counterChanges(state, evt, run, loginRun);
    for (const def of MILESTONES) {
        const value = next[def.id];
        if (value === undefined)
            continue;
        const before = counterOf(state, def.id);
        const after = Math.min(COUNTER_CAP, value);
        if (after === before)
            continue;
        counters[def.id] = after;
        changed = true;
        const from = levelsForValue(def, before);
        const to = levelsForValue(def, after);
        for (let i = from; i < to; i += 1) {
            earned.push({ id: def.id, key: levelKey(def, i), index: i, coins: levelCoins(def, i) });
        }
    }
    if (!changed)
        return { state, earned, changed };
    const dates = { ...state.earned };
    if (today !== null)
        for (const level of earned)
            dates[_a = level.key] ?? (dates[_a] = today);
    return {
        state: {
            counters,
            claimed: state.claimed,
            run,
            loginRun,
            ...(Object.keys(dates).length > 0 ? { earned: dates } : {}),
        },
        earned,
        changed,
    };
}
/**
 * Counters for a save with no `milestones` yet. Every value is a LOWER bound
 * the save can prove (retroactive where progress was kept, D-list "Counter"
 * column). Blocks cleared, coins spent and wins without power-ups were never
 * kept, so they start at 0.
 */
export function seedMilestones(seed) {
    const q = (id) => seed.quests?.progress[id] ?? 0;
    const solved = seed.solvedByTrack;
    const totalSolved = Object.values(solved).reduce((a, b) => a + b, 0);
    const counters = {
        wins: Math.max(totalSolved, q('win_50'), q('win_5')),
        reach_puzzle: Math.max(seed.maxPuzzle, q('reach_puzzle_20')),
        win_run: q('win_streak_3'),
        moves_spare: q('win_moves_spare'),
        powered_gates: q('powered_gate_3'),
        powerups_used: Math.max(seed.powerupsUsed, q('use_powerup')),
        dailies_cleared: Math.max(seed.dailyStreak, q('daily_finish')),
        daily_streak: Math.max(seed.dailyStreak, q('daily_streak_3')),
        login_streak: seed.loginDay,
        medium_wins: solved.intermediate ?? 0,
        hard_wins: solved.expert ?? 0,
        howto_explored: seed.howToRewarded ? 1 : 0,
    };
    for (const [id, n] of Object.entries(counters)) {
        if (!(n > 0))
            delete counters[id];
        else
            counters[id] = Math.min(COUNTER_CAP, Math.floor(n));
    }
    return {
        counters,
        claimed: [],
        run: Math.max(0, seed.quests?.run ?? 0),
        loginRun: Math.max(0, seed.loginDay),
    };
}
/** A level key back to its milestone and rung index, or null when it names no real level. */
export function parseLevelKey(key) {
    const m = /^([a-z_]+?)_(bronze|silver|gold|platinum|p([1-9]\d{0,2}))$/.exec(key);
    if (!m)
        return null;
    const def = milestoneDef(m[1]);
    if (!def)
        return null;
    if (m[3] !== undefined) {
        const n = Number(m[3]);
        return def.every > 0 && n >= 1 && n <= MILESTONE_MAX_STEP
            ? { def, index: def.tiers.length + n - 1 }
            : null;
    }
    const tier = MILESTONE_TIER_NAMES.indexOf(m[2]);
    return tier < def.tiers.length ? { def, index: tier } : null;
}
/** Every key a valid state may hold, for the save validator. */
export function isMilestoneKey(key) {
    return parseLevelKey(key) !== null;
}
export { COUNTER_CAP as MILESTONE_COUNTER_CAP };
